import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import {
  catchError,
  map,
  switchMap,
  concatMap,
  withLatestFrom,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { Store, select } from '@ngrx/store';

import { EmailService } from '../services';
import {
  GetStatistics,
  GetStatisticsSuccess,
  GetStatisticsFail,
  EmailActionTypes,
  GetStatisticsOverTime,
  GetStatisticsOverTimeSuccess,
  GetStatisticsOverTimeFail,
  GetDomainStatistics,
  GetDomainStatisticsSuccess,
  GetDomainStatisticsFail,
  GetSendingsStatistics,
  GetSendingsStatisticsSuccess,
  GetSendingsStatisticsFail,
  DownloadCSV,
  DownloadCSVSuccess,
  DownloadCSVFailure,
} from '../actions/email.actions';
import { State } from '../reducers/delivery-statistics.reducer';
import { DateRange } from '../models';
import { getCurrentParameters } from '../reducers';

@Injectable()
export class EmailEffects {
  @Effect()
  getStatistics$ = this.actions$.pipe(
    ofType(EmailActionTypes.GetStatistics),
    switchMap(({ payload: { dateRange, accountId } }: GetStatistics) =>
      this.service.getStatistics(dateRange, accountId).pipe(
        map((statistics) => new GetStatisticsSuccess(statistics)),
        catchError((error) => {
          this.toastr.error('Could not fetch statistics', 'Request failed');
          return of(new GetStatisticsFail(error));
        }),
      ),
    ),
  );

  @Effect()
  getStatisticsOverTime$ = this.actions$.pipe(
    ofType(EmailActionTypes.GetStatisticsOverTime),
    switchMap(({ payload: { timeSpans, accountId } }: GetStatisticsOverTime) =>
      this.service.getStatisticsOverTime(timeSpans, accountId).pipe(
        map((statistics) => new GetStatisticsOverTimeSuccess(statistics)),
        catchError((error) => {
          this.toastr.error('Could not fetch statistics', 'Request failed');
          return of(new GetStatisticsOverTimeFail(error));
        }),
      ),
    ),
  );

  @Effect()
  getDomainStatistics$ = this.actions$.pipe(
    ofType(EmailActionTypes.GetDomainStatistics),
    switchMap(({ payload: { dateRange, accountId } }: GetDomainStatistics) =>
      this.service.getDomainStatistics(dateRange, accountId).pipe(
        map((statistics) => new GetDomainStatisticsSuccess(statistics)),
        catchError((error) => {
          this.toastr.error(
            'Could not fetch domain statistics',
            'Request failed',
          );
          return of(new GetDomainStatisticsFail(error));
        }),
      ),
    ),
  );

  @Effect()
  getSendingsStatistics$ = this.actions$.pipe(
    ofType(EmailActionTypes.GetSendingsStatistics),
    switchMap(({ payload: { dateRange, accountId } }: GetSendingsStatistics) =>
      this.service.getSendingsStatistics(dateRange, accountId).pipe(
        map((statistics) => new GetSendingsStatisticsSuccess(statistics)),
        catchError((error) => {
          this.toastr.error(
            'Could not fetch sendings statistics',
            'Request failed',
          );
          return of(new GetSendingsStatisticsFail(error));
        }),
      ),
    ),
  );

  @Effect()
  downloadCSV = this.actions$.pipe(
    ofType(EmailActionTypes.DownloadCSV),
    withLatestFrom(this.store.pipe(select(getCurrentParameters))),
    concatMap(
      ([{ payload }, { accountId, dateRange }]: [
        DownloadCSV,
        { accountId: string; dateRange: DateRange },
      ]) =>
        this.service.getStatisticsCSV(payload, accountId, dateRange).pipe(
          map((csv) => {
            this.download(csv);
            return new DownloadCSVSuccess();
          }),
          catchError((error) => {
            this.toastr.error('Could not download CSV', 'Request failed');
            return of(new DownloadCSVFailure(error));
          }),
        ),
    ),
  );

  download(file: ArrayBuffer) {
    const blob = new Blob([file], { type: 'text/csv;charset=utf-8;' });
    const anchorEl = document.createElement('a');
    const fileName = `${Date.now()}.csv`;
    document.body.appendChild(anchorEl);

    if (navigator.msSaveBlob) {
      navigator.msSaveBlob(blob, fileName);
    } else {
      const url = window.URL.createObjectURL(blob);
      anchorEl.href = url;
      anchorEl.download = fileName;
      anchorEl.click();
      window.URL.revokeObjectURL(url);
    }

    document.body.removeChild(anchorEl);
  }

  constructor(
    private readonly actions$: Actions,
    private readonly service: EmailService,
    private readonly toastr: ToastrService,
    private readonly store: Store<State>,
  ) {}
}
